import type { PrescriptionLine, SetMeasure } from '@/lib/prescription';

/**
 * The demo programme is the real shape of a personal trainer's block, taken
 * from the supplied spreadsheet export ("Mahesh 1.1-1.6 — BLOCK 1.5").
 * The point of the section that renders it is that a lifter gets this layout
 * without ever seeing a spreadsheet: day tabs, superset groups, per-side reps,
 * tempo, RPE targets, prescribed load and the RIR legend.
 */

export interface ProgrammeExercise {
  id: string;
  movement: string;
  variant: string | null;
  equipment: string;
  muscleGroup: string;
  modifications: string | null;
  line: PrescriptionLine;
  /** What the lifter actually logged in week 1 (LOAD / REPS pairs in the sheet). */
  logged: Array<{ weightKg: number; reps: number; measure?: SetMeasure; distanceM?: number | null }>;
  note: string | null;
}

export interface ProgrammeDay {
  id: string;
  dayIndex: number;
  label: string;
  focus: 'LOWER' | 'UPPER';
  exercises: ProgrammeExercise[];
}

const line = (
  supersetGroup: number | null,
  prescribedSets: number,
  repMin: number | null,
  repMax: number | null,
  overrides: Partial<PrescriptionLine> = {},
): PrescriptionLine => ({
  supersetGroup,
  prescribedSets,
  repMin,
  repMax,
  measure: 'reps',
  perSide: false,
  isBodyweight: false,
  tempo: null,
  targetRpe: 7,
  percent1rm: null,
  targetWeightKg: null,
  targetDistanceM: null,
  targetDurationS: null,
  restSeconds: 120,
  ...overrides,
});

export const PROGRAMME_BLOCK = {
  name: 'Block 1.5',
  client: 'Mahesh',
  startDate: '2026-05-12',
  cadence: 'MONTHLY',
  daysPerWeek: 4,
};

/** Which days of the current week are behind the lifter — the dashboard's calendar, compacted. */
export const WEEK_STATE: Array<{ dayIndex: number; state: 'done' | 'next' | 'upcoming' }> = [
  { dayIndex: 1, state: 'done' },
  { dayIndex: 2, state: 'done' },
  { dayIndex: 3, state: 'next' },
  { dayIndex: 4, state: 'upcoming' },
];

export const PROGRAMME_DAYS: ProgrammeDay[] = [
  {
    id: 'day-1',
    dayIndex: 1,
    label: 'DAY 1 GYM',
    focus: 'LOWER',
    exercises: [
      {
        id: 'd1-squat',
        movement: 'SQUAT',
        variant: 'SSB',
        equipment: 'barbell',
        muscleGroup: 'quads',
        modifications: null,
        line: line(1, 3, 8, 8, { targetWeightKg: 100, tempo: '3-1-1-0', restSeconds: 180 }),
        logged: [
          { weightKg: 60, reps: 10 },
          { weightKg: 100, reps: 7 },
        ],
        note: 'Safety-bar squat. Brace, sit between the hips, no bounce out of the hole.',
      },
      {
        id: 'd1-hip-airplane',
        movement: 'HIP AIRPLANE',
        variant: null,
        equipment: 'bodyweight',
        muscleGroup: 'glutes',
        modifications: null,
        line: line(1, 3, 6, 6, { perSide: true, isBodyweight: true, restSeconds: 60 }),
        logged: [
          { weightKg: 0, reps: 6 },
          { weightKg: 0, reps: 6 },
        ],
        note: 'Slow. Own the end range before you load it.',
      },
      {
        id: 'd1-split-squat',
        movement: 'SPLIT SQUAT',
        variant: 'BULGARIAN / RFE',
        equipment: 'barbell',
        muscleGroup: 'quads',
        modifications: 'BARBELL',
        line: line(2, 3, 6, 6, { perSide: true, targetWeightKg: 40, restSeconds: 120 }),
        logged: [
          { weightKg: 20, reps: 8 },
          { weightKg: 40, reps: 8 },
        ],
        note: 'Front shin vertical. Weight through the mid-foot.',
      },
      {
        id: 'd1-rdl-single',
        movement: 'RDL',
        variant: 'SINGLE LEG',
        equipment: 'dumbbell',
        muscleGroup: 'hamstrings',
        modifications: 'DB',
        line: line(3, 3, 8, 8, { perSide: true, targetWeightKg: 15, restSeconds: 90 }),
        logged: [
          { weightKg: 15, reps: 8 },
          { weightKg: 15, reps: 8 },
        ],
        note: 'Hinge, do not squat. Hips square to the floor.',
      },
      {
        id: 'd1-heel-raise',
        movement: 'HEEL RAISE',
        variant: 'BILATERAL',
        equipment: 'machine',
        muscleGroup: 'calves',
        modifications: 'SSB / ON BOX / SINGLE LEG',
        line: line(3, 3, 10, 10, { targetWeightKg: 62.5, restSeconds: 60 }),
        logged: [
          { weightKg: 60, reps: 10 },
          { weightKg: 60, reps: 10 },
        ],
        note: 'Full stretch at the bottom, two-second pause at the top.',
      },
    ],
  },
  {
    id: 'day-2',
    dayIndex: 2,
    label: 'DAY 2 GYM',
    focus: 'UPPER',
    exercises: [
      {
        id: 'd2-bench',
        movement: 'BENCH PRESS',
        variant: 'BARBELL',
        equipment: 'barbell',
        muscleGroup: 'chest',
        modifications: null,
        line: line(1, 3, 8, 10, { targetWeightKg: 57.5, tempo: '2-1-1-0', restSeconds: 150 }),
        logged: [
          { weightKg: 40, reps: 10 },
          { weightKg: 50, reps: 10 },
        ],
        note: 'Shoulder blades pinned. Bar to the lower sternum.',
      },
      {
        id: 'd2-lat-pulldown',
        movement: 'LAT PULL DOWNS',
        variant: 'NEUTRAL GRIP',
        equipment: 'cable',
        muscleGroup: 'back',
        modifications: null,
        line: line(1, 3, 10, 10, { targetWeightKg: 57.5, restSeconds: 90 }),
        logged: [
          { weightKg: 30, reps: 10 },
          { weightKg: 30, reps: 10 },
        ],
        note: 'Chest up, elbows down and back. No leaning back to finish.',
      },
      {
        id: 'd2-shoulder-press',
        movement: 'SHOULDER PRESS',
        variant: 'SINGLE ARM, HALF KNEELING, DB',
        equipment: 'dumbbell',
        muscleGroup: 'shoulders',
        modifications: null,
        line: line(2, 3, 8, 10, { perSide: true, targetWeightKg: 15, restSeconds: 90 }),
        logged: [
          { weightKg: 12.5, reps: 8 },
          { weightKg: 12.5, reps: 8 },
        ],
        note: 'Ribs down, glute switched on. Press past the ear.',
      },
      {
        id: 'd2-row',
        movement: 'ROW',
        variant: 'SINGLE ARM, KNEE SUPPORTED, DB',
        equipment: 'dumbbell',
        muscleGroup: 'back',
        modifications: null,
        line: line(2, 3, 8, 10, { perSide: true, targetWeightKg: 27.5, restSeconds: 90 }),
        logged: [
          { weightKg: 17.5, reps: 10 },
          { weightKg: 17.5, reps: 10 },
        ],
        note: 'No torso rotation. Drive the elbow to the hip.',
      },
      {
        id: 'd2-ski-erg',
        movement: 'SKI ERG',
        variant: null,
        equipment: 'machine',
        muscleGroup: 'full_body',
        modifications: null,
        line: line(3, 1, null, null, {
          measure: 'distance_m',
          targetDistanceM: 1000,
          targetRpe: 8,
          restSeconds: 0,
        }),
        logged: [{ weightKg: 0, reps: 0, measure: 'distance_m', distanceM: 1000 }],
        note: 'Time it. Same distance, faster every block.',
      },
    ],
  },
  {
    id: 'day-3',
    dayIndex: 3,
    label: 'DAY 3 GYM',
    focus: 'LOWER',
    exercises: [
      {
        id: 'd3-trap-deadlift',
        movement: 'DEADLIFT',
        variant: 'TRAP BAR',
        equipment: 'barbell',
        muscleGroup: 'hamstrings',
        modifications: null,
        line: line(1, 3, 6, 8, { targetWeightKg: 130, tempo: '2-0-1-0', restSeconds: 210 }),
        logged: [
          { weightKg: 110, reps: 10 },
          { weightKg: 110, reps: 10 },
        ],
        note: 'Wedge in, push the floor away. Stop when the bar slows.',
      },
      {
        id: 'd3-rdl',
        movement: 'RDL',
        variant: 'BARBELL',
        equipment: 'barbell',
        muscleGroup: 'hamstrings',
        modifications: null,
        line: line(1, 3, 8, 8, { targetWeightKg: 80, restSeconds: 120 }),
        logged: [
          { weightKg: 60, reps: 10 },
          { weightKg: 80, reps: 8 },
        ],
        note: 'Bar close to the legs. Hips back, not down.',
      },
      {
        id: 'd3-single-leg-box',
        movement: 'SQUAT',
        variant: 'SINGLE LEG, TO BOX',
        equipment: 'bodyweight',
        muscleGroup: 'quads',
        modifications: null,
        line: line(2, 3, 8, 8, { perSide: true, isBodyweight: true, restSeconds: 90 }),
        logged: [
          { weightKg: 0, reps: 8 },
          { weightKg: 0, reps: 8 },
        ],
        note: 'Control the descent to the box. No pushing off the floor.',
      },
      {
        id: 'd3-leg-extension',
        movement: 'LEG EXTENSIONS',
        variant: null,
        equipment: 'machine',
        muscleGroup: 'quads',
        modifications: 'SINGLE LEG',
        line: line(2, 3, 8, 8, { perSide: true, targetWeightKg: 25, restSeconds: 60 }),
        logged: [
          { weightKg: 15, reps: 10 },
          { weightKg: 15, reps: 10 },
        ],
        note: 'Pause at the top for a beat.',
      },
      {
        id: 'd3-ab-rollout',
        movement: 'ROLLOUT',
        variant: 'AB WHEEL',
        equipment: 'bodyweight',
        muscleGroup: 'core',
        modifications: null,
        line: line(2, 3, 10, 10, { isBodyweight: true, restSeconds: 60 }),
        logged: [
          { weightKg: 0, reps: 8 },
          { weightKg: 0, reps: 8 },
        ],
        note: 'Ribs down. Stop before the lower back arches.',
      },
      {
        id: 'd3-sled-pull',
        movement: 'SLED PULL',
        variant: null,
        equipment: 'machine',
        muscleGroup: 'full_body',
        modifications: null,
        line: line(3, 3, null, null, { measure: 'distance_m', targetDistanceM: 15, targetRpe: 7, restSeconds: 90 }),
        logged: [
          { weightKg: 100, reps: 0, measure: 'distance_m', distanceM: 15 },
          { weightKg: 100, reps: 0, measure: 'distance_m', distanceM: 15 },
        ],
        note: 'Heavy, short, walking pace. Rest fully between efforts.',
      },
      {
        id: 'd3-sled-push',
        movement: 'SLED PUSH',
        variant: null,
        equipment: 'machine',
        muscleGroup: 'full_body',
        modifications: null,
        line: line(3, 3, null, null, { measure: 'distance_m', targetDistanceM: 15, targetRpe: 7, restSeconds: 90 }),
        logged: [
          { weightKg: 100, reps: 0, measure: 'distance_m', distanceM: 15 },
          { weightKg: 100, reps: 0, measure: 'distance_m', distanceM: 15 },
        ],
        note: 'Low body angle, short steps, drive through the floor.',
      },
    ],
  },
  {
    id: 'day-4',
    dayIndex: 4,
    label: 'DAY 4 GYM',
    focus: 'UPPER',
    exercises: [
      {
        id: 'd4-incline-db',
        movement: 'BENCH PRESS',
        variant: 'INCLINE, DUMBBELL',
        equipment: 'dumbbell',
        muscleGroup: 'chest',
        modifications: null,
        line: line(1, 3, 8, 10, { targetWeightKg: 25, tempo: '3-0-1-0', restSeconds: 120 }),
        logged: [
          { weightKg: 25, reps: 10 },
          { weightKg: 25, reps: 10 },
        ],
        note: 'Bench at 30°. Full stretch, no clanging at the top.',
      },
      {
        id: 'd4-lat-pulldown-single',
        movement: 'LAT PULLDOWN',
        variant: 'SINGLE ARM',
        equipment: 'cable',
        muscleGroup: 'back',
        modifications: null,
        line: line(1, 3, 8, 8, { perSide: true, targetWeightKg: 32.5, restSeconds: 90 }),
        logged: [
          { weightKg: 32.5, reps: 8 },
          { weightKg: 32.5, reps: 8 },
        ],
        note: 'Reach long at the top, no torso twist.',
      },
      {
        id: 'd4-viking-press',
        movement: 'VIKING PRESS',
        variant: null,
        equipment: 'machine',
        muscleGroup: 'shoulders',
        modifications: null,
        line: line(2, 3, 10, 10, { targetWeightKg: 35, restSeconds: 90 }),
        logged: [
          { weightKg: 35, reps: 10 },
          { weightKg: 35, reps: 10 },
        ],
        note: 'Press up and slightly forward. Keep the ribs stacked.',
      },
      {
        id: 'd4-chest-supported-row',
        movement: 'ROW',
        variant: 'CHEST SUPPORTED, BARBELL',
        equipment: 'barbell',
        muscleGroup: 'back',
        modifications: null,
        line: line(2, 3, 10, 10, { targetWeightKg: 35, restSeconds: 90 }),
        logged: [
          { weightKg: 35, reps: 10 },
          { weightKg: 35, reps: 10 },
        ],
        note: 'Chest stays glued to the pad.',
      },
      {
        id: 'd4-face-pull',
        movement: 'FACEPULLS',
        variant: 'CABLE',
        equipment: 'cable',
        muscleGroup: 'shoulders',
        modifications: null,
        line: line(3, 3, 10, 10, { targetWeightKg: 67.5, restSeconds: 60 }),
        logged: [
          { weightKg: 67.5, reps: 10 },
          { weightKg: 67.5, reps: 10 },
        ],
        note: 'Rope to the forehead, external rotation at the end.',
      },
      {
        id: 'd4-triceps',
        movement: 'TRICEP PUSHDOWNS',
        variant: 'CABLES',
        equipment: 'cable',
        muscleGroup: 'triceps',
        modifications: null,
        line: line(4, 3, 10, 10, { targetWeightKg: 37.5, restSeconds: 60 }),
        logged: [
          { weightKg: 37.5, reps: 10 },
          { weightKg: 37.5, reps: 10 },
        ],
        note: 'Elbows pinned to the ribs.',
      },
      {
        id: 'd4-biceps',
        movement: 'SEATED BICEP CURLS',
        variant: null,
        equipment: 'dumbbell',
        muscleGroup: 'biceps',
        modifications: 'DB',
        line: line(4, 3, 10, 10, { targetWeightKg: 10, restSeconds: 60 }),
        logged: [
          { weightKg: 10, reps: 10 },
          { weightKg: 10, reps: 10 },
        ],
        note: 'No swing. Control the lowering.',
      },
    ],
  },
];

/** Group rows so a superset renders as one visual block, exactly like the sheet's numbering. */
export function groupBySuperset(day: ProgrammeDay): Array<{ group: number | null; exercises: ProgrammeExercise[] }> {
  const groups = new Map<number | null, ProgrammeExercise[]>();
  for (const exercise of day.exercises) {
    const key = exercise.line.supersetGroup;
    const bucket = groups.get(key);
    if (bucket) bucket.push(exercise);
    else groups.set(key, [exercise]);
  }
  return [...groups.entries()].map(([group, exercises]) => ({ group, exercises }));
}
